
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, isOnline } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { safeSelect } from "@/utils/supabaseHelpers";

interface GroupDataFetcherProps {
  groupId: string | undefined;
  setSession: (session: any) => void;
  setGroupName: (name: string) => void;
  setGroupStatus: (status: string) => void;
  fetchMembers: () => Promise<void>;
  fetchExpenses: () => Promise<void>;
  fetchSettlements: () => Promise<void>;
}

export const GroupDataFetcher = ({
  groupId,
  setSession,
  setGroupName,
  setGroupStatus,
  fetchMembers,
  fetchExpenses,
  fetchSettlements
}: GroupDataFetcherProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isMounted = useRef(true);
  const lastFetchTime = useRef<number>(0);
  const groupCacheKey = `group-data-${groupId}`;
  const initialDataLoaded = useRef<boolean>(false);
  
  // Less aggressive throttling for better data freshness
  const throttledFetch = useCallback(async (fetchFn: () => Promise<void>) => {
    const now = Date.now();
    const throttleDelay = 2000; // 2 seconds minimum between fetches
    
    if (now - lastFetchTime.current < throttleDelay) {
      return;
    }
    
    lastFetchTime.current = now;
    await fetchFn();
  }, []);

  const loadAllData = useCallback(async (forceLoad = false) => {
    if (!groupId || !isMounted.current) return;
    
    // If we've already loaded initial data and we're not forcing a reload, return
    if (initialDataLoaded.current && !forceLoad) return;
    
    console.log("Loading all group data (expenses, members, settlements)...");
    
    // Try all fetches in parallel
    try {
      await Promise.all([
        throttledFetch(fetchMembers),
        throttledFetch(fetchExpenses),
        throttledFetch(fetchSettlements)
      ]);
      
      initialDataLoaded.current = true;
      console.log("Initial data load complete");
      
      // Reset initial load flag once everything is loaded
      if (isInitialLoad && isMounted.current) {
        setIsInitialLoad(false);
      }
      
      // Reset retry count on success
      if (isMounted.current) {
        setRetryCount(0);
      }
    } catch (error) {
      console.error("Error loading group data:", error);
      
      // Only increment retry count on initial load
      if (isInitialLoad && isMounted.current) {
        setRetryCount(prev => prev + 1);
      }
      
      // Only show toast on initial load failure
      if (isInitialLoad && isMounted.current) {
        toast({
          title: t('common.error'),
          description: t('groups.dataLoadError', 'Could not load all group data. Some features may be limited.'),
          variant: "destructive",
        });
      }
    }
  }, [groupId, throttledFetch, fetchMembers, fetchExpenses, fetchSettlements, isInitialLoad, setRetryCount, toast, t]);

  const fetchGroupData = useCallback(async () => {
    if (!groupId || !isMounted.current) return;
    
    try {
      // Check for cached group data first
      const cachedData = localStorage.getItem(groupCacheKey);
      let cachedGroup = null;
      
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (parsed && parsed.timestamp && (Date.now() - parsed.timestamp < 10 * 60 * 1000)) {
            cachedGroup = parsed.data;
            // Set from cache immediately while we load fresh data
            setGroupName(cachedGroup.name || "");
            setGroupStatus(cachedGroup.status || 'active');
          }
        } catch (e) {
          console.warn('Error parsing cached group data', e);
        }
      }
      
      // Get current session
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        console.log("No session found, redirecting to login");
        navigate('/login');
        return;
      }
      
      // Set session
      setSession(sessionData.session);

      // Fetch group data with retry logic and proper type handling
      const response = await safeSelect<{name: string, status: string}>(
        'groups',
        { id: groupId },
        { single: true }
      );
      
      if (!response.success || !response.data) {
        // If we have cached data, keep using it
        if (cachedGroup) {
          console.log("Using cached group data because fresh data couldn't be loaded");
          
          // Try to load all other data even if group metadata failed
          loadAllData(true);
          return;
        }
        
        console.error("Group not found");
        toast({
          title: t('common.error'),
          description: t('groups.notFound', 'The requested group could not be found.'),
          variant: "destructive",
        });
        navigate('/app');
        return;
      }
      
      // Update cache with fresh data
      localStorage.setItem(groupCacheKey, JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
      
      setGroupName(response.data.name || "");
      setGroupStatus(response.data.status || 'active');
      
      // Always attempt to load all data on initial page load
      loadAllData(true);
      
    } catch (error) {
      console.error("Error in fetchGroupData:", error);
      
      // Increment retry count and attempt retry if under MAX_RETRIES
      if (retryCount < MAX_RETRIES && isMounted.current) {
        setRetryCount(prev => prev + 1);
        
        // Wait before retrying with exponential backoff
        setTimeout(() => {
          if (isMounted.current) {
            fetchGroupData();
          }
        }, 1000 * Math.pow(2, retryCount));
      } else if (isInitialLoad && isMounted.current) {
        // Only show toast on initial load failure, not on reconnect attempts
        toast({
          title: t('common.error'),
          description: t('groups.loadError', 'Could not load group details. Please try again.'),
          variant: "destructive",
        });
      }
    }
  }, [groupId, navigate, setSession, setGroupName, setGroupStatus, toast, t, retryCount, isInitialLoad, loadAllData, groupCacheKey]);
  
  // Initial data fetch
  useEffect(() => {
    isMounted.current = true;
    initialDataLoaded.current = false;
    
    if (groupId) {
      fetchGroupData();
    }
    
    return () => {
      isMounted.current = false;
    };
  }, [groupId, fetchGroupData]);

  // Add a forced reload on visibility change (returning to the app from background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && groupId && isMounted.current) {
        console.log("App returned to foreground, refreshing data...");
        loadAllData(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [groupId, loadAllData]);

  // Add an event listener for online/offline status
  useEffect(() => {
    let lastOnlineEvent = 0;
    
    const handleOnline = () => {
      const now = Date.now();
      // Prevent multiple reconnect events fired in quick succession
      if (now - lastOnlineEvent < 5000) return;
      
      lastOnlineEvent = now;
      console.log("Device is back online, attempting to refresh data...");
      
      // Only refetch if we're mounted and have a group ID
      if (isMounted.current && groupId) {
        // Reset retry count when coming back online
        setRetryCount(0);
        loadAllData(true);
      }
    };

    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [groupId, loadAllData, setRetryCount]);

  return null;
};
